import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { isBlank, notBlank } from "../../util/ext";
import {
  equals,
  find,
  lensPath,
  map,
  mergeRight,
  pick,
  pipe,
  prop,
  propEq,
  set,
  startsWith,
} from "ramda";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import {
  validateLocation,
  validateLocationField,
} from "../organizationValidation";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import { Skeleton } from "@mui/material";
import { useErrorBox } from "./use-errorBox";
import {
  useCountries,
  useStates,
} from "../../manage-authentication/regionService";
import {
  useLocationSearch,
  useOrganizationLocationCreate,
  useOrganizationLocationUpdate,
} from "../organizationServices";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

const OrganizationLocationDialog = ({
  open,
  organizationId,

  organizationName,
  location,
  onCancel,
  onSave,
}) => {
  const classes = useStyles();
  const [fields, setFields] = useState({});
  useEffect(() => {
    if (open && notBlank(location)) {
      setFields(
        pick(
          [
            "locationId",
            "addressLineOne",
            "addressLineTwo",
            "locality",
            "country",
            "administrativeArea",
            "postalCode",
            "coordinates",
          ],
          location,
        ),
      );
    } else {
      setFields({});
    }
  }, [open, location]);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const countries = useCountries();
  useEffect(() => {
    if (countries.isSuccess && notBlank(countries.data)) {
      if (notBlank(fields.country)) {
        const country = find(propEq("code", fields.country), countries.data);
        setSelectedCountry(country);
      }
    }
  }, [countries.isSuccess, countries.data, fields.country]);

  const states = useStates(selectedCountry);
  const [selectedState, setSelectedState] = useState(null);
  const [stateList, setStateList] = useState([]);
  useEffect(() => {
    if (states.isSuccess && notBlank(states.data)) {
      const list = map(
        state => ({
          code: state.code.slice(-2),
          name: state.name,
        }),
        states.data,
      );
      setStateList(list);
      if (notBlank(fields.administrativeArea)) {
        const state = find(
          pipe(prop("name"), startsWith(fields.administrativeArea)),
          list,
        );
        setSelectedState(state);
      }
    } else {
      setStateList([]);
    }
  }, [states.isSuccess, states.data, fields.administrativeArea]);

  const [searchString, setSearchString] = useState(null);
  const locationSearchResponse = useLocationSearch(searchString);
  useEffect(() => {
    if (
      locationSearchResponse.isSuccess &&
      notBlank(locationSearchResponse.data)
    ) {
      setFields(prev => mergeRight(prev, locationSearchResponse.data));
    }
  }, [locationSearchResponse.isSuccess, locationSearchResponse.data]);

  const handleLookupAddressClick = useCallback(() => {
    setSearchString(organizationName);
  }, [organizationName]);

  const handleLookupCoordinatesClick = useCallback(() => {
    const search = `${fields.addressLineOne} ${fields.locality} ${fields.administrativeArea} ${fields.country}`;
    setSearchString(search);
  }, [fields]);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleBlur = (field, value) => {
    const errors = validateLocationField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleCountryChange = (_event, country) => {
    setSelectedCountry(country);
    setFields(prev => set(lensPath(["country"]), country.code, prev));
  };

  const handleStateChange = (_event, state) => {
    setSelectedState(state);
    setFields(prev => set(lensPath(["administrativeArea"]), state.name, prev));
  };

  // const updateDetails = useOrganizationDetailsUpdate();
  const updateLocation = useOrganizationLocationUpdate();
  const createLocation = useOrganizationLocationCreate();
  const handleApply = useCallback(async () => {
    const validationErrors = validateLocation(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      const variables = {
        resourceIds: { orgId: organizationId },
        data: fields,
      };

      try {
        if (notBlank(fields.locationId)) {
          await updateLocation.mutateAsync(variables);
        } else {
          await createLocation.mutateAsync(variables);
        }

        onSave(fields);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [
    fields,
    organizationId,
    updateLocation,
    createLocation,
    onSave,
    setValidationErrors,
    setGeneralError,
  ]);

  return (
    <Dialog open={open}>
      <DialogTitle id="edit-organization-detail-dialog-title">
        Organization Location
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter Organization address or lookup using Organization name.
        </DialogContentText>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item sm={12}>
            <Button variant={"text"} onClick={handleLookupAddressClick}>
              Lookup Address
            </Button>
          </Grid>
          <Grid item sm={12}>
            <TextField
              id="address-one"
              fullWidth
              label="Address Line One"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.addressLineOne || ""}
              onChange={handleTextChange(["addressLineOne"])}
              error={hasValidationError("addressLineOne")}
              helperText={firstValidationError("addressLineOne")}
              onBlur={() => handleBlur("addressLineOne", fields.addressLineOne)}
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="address-two"
              fullWidth
              label="Address Line Two"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.addressLineTwo || ""}
              onChange={handleTextChange(["addressLineTwo"])}
              error={hasValidationError("addressLineTwo")}
              helperText={firstValidationError("addressLineTwo")}
              onBlur={() => handleBlur("addressLineTwo", fields.addressLineTwo)}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="city"
              fullWidth
              label="City"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.locality || ""}
              onChange={handleTextChange(["locality"])}
              error={hasValidationError("locality")}
              helperText={firstValidationError("locality")}
              onBlur={() => handleBlur("locality", fields.locality)}
            />
          </Grid>

          <Grid item sm={6}>
            {countries.isLoading || isBlank(countries.data) ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                options={countries.data}
                getOptionLabel={country =>
                  notBlank(country) && `${country.name}`
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.code, val.code)
                }
                value={selectedCountry}
                onChange={handleCountryChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"country-" + Math.random()}
                      label="Country"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>

          <Grid item sm={6}>
            <Autocomplete
              disabled={isBlank(selectedCountry)}
              options={stateList}
              getOptionLabel={state => notBlank(state) && `${state.name}`}
              isOptionEqualToValue={(opt, val) =>
                notBlank(val) && equals(opt.code, val.code)
              }
              value={selectedState}
              onChange={handleStateChange}
              renderInput={params => {
                params.inputProps["aria-autocomplete"] = "none";
                params.inputProps["id"] = null;
                return (
                  <TextField
                    {...params}
                    type="text"
                    name={"administrativeArea-" + Math.random()}
                    label="State/Province"
                    fullWidth
                  />
                );
              }}
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="postal-code"
              fullWidth
              label="Postal Code"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.postalCode || ""}
              onChange={handleTextChange(["postalCode"])}
              error={hasValidationError("postalCode")}
              helperText={firstValidationError("postalCode")}
              onBlur={() => handleBlur("postalCode", fields.postalCode)}
            />
          </Grid>
        </Grid>

        <DialogContentText component="div" style={{ marginTop: 40 }}>
          Enter Location Coordinates or lookup using address.
        </DialogContentText>

        <Grid container spacing={6}>
          <Grid item sm={12}>
            <Button variant={"text"} onClick={handleLookupCoordinatesClick}>
              Lookup Coordinates
            </Button>
          </Grid>
          <Grid item sm={6}>
            <TextField
              id="latitude"
              fullWidth
              label="Latitude"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields?.coordinates?.latitude ?? ""}
              onChange={handleTextChange(["coordinates", "latitude"])}
              error={hasValidationError("coordinates.latitude")}
              helperText={firstValidationError("coordinates.latitude")}
              onBlur={() =>
                handleBlur(
                  "coordinates.latitude",
                  fields?.coordinates?.latitude,
                )
              }
            />
          </Grid>

          <Grid item sm={6}>
            <TextField
              id="longitude"
              fullWidth
              label="Longitude"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields?.coordinates?.longitude ?? ""}
              onChange={handleTextChange(["coordinates", "longitude"])}
              error={hasValidationError("coordinates.longitude")}
              helperText={firstValidationError("coordinates.longitude")}
              onBlur={() =>
                handleBlur(
                  "coordinates.longitude",
                  fields?.coordinates?.longitude,
                )
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationLocationDialog;
